import { useState } from 'react';
import { Download, FileText, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScanRecord, AnalyticsData } from '@/hooks/useQRAnalytics';
import { toast } from 'sonner';

interface ExportAnalyticsProps {
  qrName: string;
  analytics: AnalyticsData;
  scans: ScanRecord[];
}

export function ExportAnalytics({ qrName, analytics, scans }: ExportAnalyticsProps) {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = () => {
    const headers = [
      'Scanned At',
      'Country',
      'City',
      'Device Type',
      'Operating System',
      'Browser',
      'IP Address',
      'Referrer',
    ];

    const rows = scans.map(scan => [
      format(new Date(scan.scanned_at), 'yyyy-MM-dd HH:mm:ss'),
      scan.country || '',
      scan.city || '',
      scan.device_type || '',
      scan.os || '',
      scan.browser || '',
      scan.ip_address || '',
      scan.referrer || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${qrName.replace(/\s+/g, '-')}-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success('CSV exported successfully!');
  };

  const exportPDF = async () => {
    setIsExporting(true);
    
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(20);
      doc.text(`QR Analytics Report: ${qrName}`, 20, 20);
      
      // Date
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated on ${format(new Date(), 'MMMM d, yyyy')}`, 20, 30);
      
      // Summary Section
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Summary', 20, 45);
      
      doc.setFontSize(10);
      const summaryY = 55;
      doc.text(`Total Scans: ${analytics.totalScans}`, 20, summaryY);
      doc.text(`Scans Today: ${analytics.scansToday}`, 20, summaryY + 7);
      doc.text(`Scans This Week: ${analytics.scansThisWeek}`, 20, summaryY + 14);
      doc.text(`Scans This Month: ${analytics.scansThisMonth}`, 20, summaryY + 21);
      doc.text(`Average Scans/Day: ${analytics.avgScansPerDay}`, 20, summaryY + 28);
      doc.text(`Unique Visitors: ~${analytics.uniqueIPs}`, 20, summaryY + 35);
      
      if (analytics.lastScanAt) {
        doc.text(`Last Scan: ${format(new Date(analytics.lastScanAt), 'MMM d, yyyy HH:mm')}`, 20, summaryY + 42);
      }
      
      // Recent scans table
      doc.setFontSize(14);
      doc.text('Recent Scans', 20, summaryY + 60);
      
      doc.setFontSize(8);
      const tableY = summaryY + 70;
      const colWidths = [35, 30, 30, 25, 25, 30];
      const headers = ['Date', 'Country', 'City', 'Device', 'OS', 'Browser'];
      
      // Table header
      let xPos = 20;
      headers.forEach((header, i) => {
        doc.text(header, xPos, tableY);
        xPos += colWidths[i];
      });
      
      // Table rows (first 20)
      scans.slice(0, 20).forEach((scan, rowIndex) => {
        const y = tableY + 7 + rowIndex * 6;
        xPos = 20;
        
        const row = [
          format(new Date(scan.scanned_at), 'MMM d, HH:mm'),
          scan.country || 'Unknown',
          scan.city || 'Unknown',
          scan.device_type || 'Unknown',
          scan.os || 'Unknown',
          scan.browser || 'Unknown',
        ];
        
        row.forEach((cell, i) => {
          const truncated = cell.length > 12 ? cell.substring(0, 10) + '...' : cell;
          doc.text(truncated, xPos, y);
          xPos += colWidths[i];
        });
      });
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text('Generated by NexQR', 20, 280);
      
      doc.save(`${qrName.replace(/\s+/g, '-')}-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF report exported!');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={isExporting}>
          <Download className="h-4 w-4 mr-2" />
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportCSV}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportPDF}>
          <FileText className="h-4 w-4 mr-2" />
          Export as PDF Report
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
